export const LAST_UPDATED = "September 18, 2026";

export const SHIPPING_POLICY = {
  route: "/shipping",
  title: "Shipping & Delivery",
  intro:
    "This policy explains how CELUSTA orders are shipped and delivered. If you have any questions, contact us at info@celusta.com.",
  sections: [
    {
      heading: "Where we ship",
      paragraphs: [
        "CELUSTA currently ships to customers in the United States only.",
        "Shipping availability and charges for your order are displayed during checkout before you complete your purchase.",
      ],
    },
    {
      heading: "Processing & delivery timing",
      paragraphs: [
        "We do not promise a specific processing or delivery timeframe. Delivery timing may vary depending on your destination, order volume, carrier operations and circumstances outside CELUSTA's control.",
      ],
    },
    {
      heading: "Order tracking",
      paragraphs: [
        "When tracking is available for your order, it will be sent to you by email after the order ships.",
      ],
    },
    {
      heading: "Delivery address",
      paragraphs: [
        "You are responsible for entering a complete and accurate delivery address at checkout.",
        "After an order has entered processing, address changes or cancellation may no longer be possible. If you notice an error, contact us at info@celusta.com as soon as possible and we will do our best to help.",
      ],
    },
    {
      heading: "Carrier delays",
      paragraphs: [
        "CELUSTA is not responsible for delays caused by shipping carriers. If your order appears delayed, contact us at info@celusta.com and we will assist you where we can.",
      ],
    },
    {
      heading: "Lost, damaged or incorrect deliveries",
      paragraphs: [
        "If your order is lost, arrives damaged or is delivered incorrectly, contact us at info@celusta.com with your order number and supporting photographs where applicable. We will work with you to resolve the issue.",
      ],
    },
  ],
};

export const RETURNS_POLICY = {
  route: "/returns",
  title: "Returns & Refunds",
  intro:
    "We want you to be happy with your CELUSTA purchase. This policy explains how returns, refunds and cancellations work.",
  sections: [
    {
      heading: "Return window",
      paragraphs: [
        "You may request a return within 30 days after delivery.",
        "Please contact us at info@celusta.com before returning anything. Returns sent without prior approval may not be accepted.",
      ],
    },
    {
      heading: "Return conditions",
      paragraphs: ["To be eligible for a return, products must be:"],
      bullets: [
        "Unused and unopened",
        "In their original packaging",
        "Accompanied by proof of purchase (such as your order number or confirmation email)",
      ],
    },
    {
      heading: "Hygiene & safety",
      paragraphs: [
        "Because CELUSTA sells personal-care products, opened or used products cannot normally be returned for hygiene and safety reasons. This does not apply where the product arrived damaged, defective or incorrect.",
      ],
    },
    {
      heading: "How to return",
      paragraphs: [
        "Once your return is approved, our customer support team will provide the approved return address and instructions.",
        "You are responsible for return shipping costs, unless the product was damaged, defective or incorrect.",
      ],
    },
    {
      heading: "Refunds",
      paragraphs: [
        "Refunds are issued to the original payment method after the return has been received and inspected.",
        "Original shipping charges are non-refundable unless CELUSTA made an error.",
        "Banks and payment providers may require additional processing time before a refund appears on your statement.",
      ],
    },
    {
      heading: "Cancellations",
      paragraphs: [
        "Orders can only be cancelled before they enter processing. Contact us at info@celusta.com as soon as possible if you wish to cancel.",
      ],
    },
    {
      heading: "Your statutory rights",
      paragraphs: [
        "These terms do not limit any mandatory consumer rights that apply to you under applicable law.",
      ],
    },
  ],
};

export const PRIVACY_POLICY = {
  route: "/privacy",
  title: "Privacy Policy",
  intro:
    "This Privacy Policy explains what information CELUSTA collects when you visit our website or place an order, how it is used and the choices you have.",
  sections: [
    {
      heading: "Information we collect",
      paragraphs: ["Depending on how you use our website, CELUSTA may collect:"],
      bullets: [
        "Contact, billing, delivery and order information",
        "Device, browser, cookie and website-usage information",
        "Customer-support communications",
        "Review information submitted through Judge.me",
      ],
    },
    {
      heading: "How we use your information",
      paragraphs: ["We may use the information we collect to:"],
      bullets: [
        "Process orders and payments",
        "Provide delivery and customer support",
        "Prevent fraud and protect the store",
        "Improve the website",
        "Measure advertising performance",
        "Send marketing only where permitted",
      ],
    },
    {
      heading: "Service providers",
      paragraphs: [
        "Service providers may process information on CELUSTA's behalf in order to run the store, including:",
      ],
      bullets: [
        "Shopify and payment providers",
        "Hosting and technical service providers",
        "Shipping and fulfillment partners",
        "Meta Pixel and related advertising services",
        "Judge.me review services",
      ],
    },
    {
      heading: "Payment information",
      paragraphs: ["CELUSTA does not sell payment-card details."],
    },
    {
      heading: "Your choices and rights",
      paragraphs: [
        "You can request access, correction or deletion of your personal information where applicable by contacting us at info@celusta.com.",
        "Cookie and tracking preferences may also be managed through your browser settings or available website controls.",
      ],
    },
    {
      heading: "Data retention",
      paragraphs: [
        "Information may be retained as needed for orders, fraud prevention, accounting and legal obligations.",
      ],
    },
    {
      heading: "Changes to this policy",
      paragraphs: [
        "This policy may be updated from time to time. The revised date is shown at the top of this page.",
      ],
    },
    {
      heading: "Contact",
      paragraphs: ["Questions about this Privacy Policy can be sent to info@celusta.com."],
    },
  ],
};

export const TERMS_POLICY = {
  route: "/terms",
  title: "Terms of Service",
  intro:
    "These Terms of Service govern your use of this website and any purchase you make. Please read them carefully.",
  sections: [
    {
      heading: "About these terms",
      paragraphs: [
        "This website is operated under the CELUSTA brand.",
        "By using the website or making a purchase, you accept these terms together with our Shipping & Delivery, Returns & Refunds and Privacy policies linked in the footer.",
      ],
    },
    {
      heading: "Accurate information",
      paragraphs: [
        "You must provide accurate account, payment and delivery information when placing an order.",
      ],
    },
    {
      heading: "Products & descriptions",
      paragraphs: [
        "Product descriptions and images are presented as accurately as reasonably possible. However, screens and packaging may vary, and product results vary between individuals.",
        "CELUSTA products are cosmetic products and are not intended to diagnose, treat, cure or prevent any disease.",
        "Please review the listed ingredients before use and discontinue use if irritation occurs.",
      ],
    },
    {
      heading: "Prices & payment",
      paragraphs: [
        "Prices are shown in USD and may change before an order is placed.",
      ],
    },
    {
      heading: "Order acceptance",
      paragraphs: [
        "CELUSTA may refuse or cancel orders that are fraudulent, abusive or incorrectly priced, and will refund any captured payment where appropriate.",
      ],
    },
    {
      heading: "Intellectual property",
      paragraphs: [
        "Website content, branding, images and text may not be copied without permission.",
      ],
    },
    {
      heading: "Applicable law",
      paragraphs: [
        "The website and our services are provided subject to applicable law. These terms do not exclude rights that cannot legally be excluded.",
      ],
    },
    {
      heading: "Contact",
      paragraphs: ["Questions about these terms can be sent to info@celusta.com."],
    },
  ],
};
